/**
 * StammdatenTab — Tab 1 des Card-Detail-Modal (CARD-01, CARD-09).
 *
 * Editierbar: Vereinsname, E-Mail, Telefon (mit tel:-Link), Ansprechpartner.
 * Read-only: Lead-Source (plenty_form/manual als Badge) + Eingangsdatum.
 *
 * Save-Button → store.updateLead(id, patch) → PATCH /leads/:id.
 * onDirty(true/false) wird an Modal-Parent gemeldet (Dirty-Check beim Close).
 *
 * CI-Farben: #E9FF57 Background + #1A1A1A Text auf Save-Button.
 */

import { useState, useEffect } from 'react';
import { Phone, Mail } from 'lucide-react';
import { useLeadBoard } from '../../store/useLeadBoard';
import type { Lead } from '../../types';

interface Props {
  lead: Lead;
  onSaved: (l: Lead) => void;
  onDirty?: (dirty: boolean) => void;
}

interface FormState {
  vereinName: string;
  vereinEmail: string;
  vereinPhone: string;
  vereinContactPerson: string;
}

function leadToForm(lead: Lead): FormState {
  return {
    vereinName: lead.vereinName ?? '',
    vereinEmail: lead.vereinEmail ?? '',
    vereinPhone: lead.vereinPhone ?? '',
    vereinContactPerson: lead.vereinContactPerson ?? '',
  };
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

function sourceLabel(source: Lead['source']): string {
  if (source === 'plenty_form') return 'Plenty-Formular';
  if (source === 'manual') return 'Manuell angelegt';
  return source;
}

export default function StammdatenTab({ lead, onSaved, onDirty }: Props) {
  const updateLead = useLeadBoard((s) => s.updateLead);
  const [form, setForm] = useState<FormState>(() => leadToForm(lead));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form wenn ein neuer Lead reinkommt
  useEffect(() => {
    setForm(leadToForm(lead));
    setError(null);
  }, [lead.id]);

  const isDirty =
    form.vereinName !== (lead.vereinName ?? '') ||
    form.vereinEmail !== (lead.vereinEmail ?? '') ||
    form.vereinPhone !== (lead.vereinPhone ?? '') ||
    form.vereinContactPerson !== (lead.vereinContactPerson ?? '');

  useEffect(() => {
    onDirty?.(isDirty);
  }, [isDirty, onDirty]);

  const setField = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (form.vereinName.trim().length === 0) {
      setError('Vereinsname darf nicht leer sein.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const updated = await updateLead(lead.id, {
        vereinName: form.vereinName.trim(),
        vereinEmail: form.vereinEmail.trim() || null,
        vereinPhone: form.vereinPhone.trim() || null,
        vereinContactPerson: form.vereinContactPerson.trim() || null,
      });
      onSaved(updated);
      onDirty?.(false);
    } catch (e: any) {
      setError(e?.message ?? 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <label className="block text-sm font-medium text-[#1A1A1A] mb-1">
            Vereinsname *
          </label>
          <input
            type="text"
            value={form.vereinName}
            onChange={(e) => setField('vereinName', e.target.value)}
            className="w-full border border-[#DAD9D9] rounded px-3 py-2 text-sm text-[#1A1A1A] focus:outline-none focus:border-[#1A1A1A]"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-[#1A1A1A] mb-1">
            E-Mail
          </label>
          <div className="flex items-center gap-2">
            <input
              type="email"
              value={form.vereinEmail}
              onChange={(e) => setField('vereinEmail', e.target.value)}
              className="flex-1 border border-[#DAD9D9] rounded px-3 py-2 text-sm text-[#1A1A1A]"
            />
            {form.vereinEmail && (
              <a
                href={`mailto:${form.vereinEmail}`}
                className="text-[#1A1A1A] hover:text-gray-600"
                aria-label="E-Mail schreiben"
              >
                <Mail className="w-4 h-4" />
              </a>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-[#1A1A1A] mb-1">
            Telefon
          </label>
          <div className="flex items-center gap-2">
            <input
              type="tel"
              value={form.vereinPhone}
              onChange={(e) => setField('vereinPhone', e.target.value)}
              className="flex-1 border border-[#DAD9D9] rounded px-3 py-2 text-sm text-[#1A1A1A]"
            />
            {form.vereinPhone && (
              <a
                href={`tel:${form.vereinPhone}`}
                className="text-[#1A1A1A] hover:text-gray-600"
                aria-label="Telefonat starten"
              >
                <Phone className="w-4 h-4" />
              </a>
            )}
          </div>
        </div>

        <div className="col-span-2">
          <label className="block text-sm font-medium text-[#1A1A1A] mb-1">
            Ansprechpartner
          </label>
          <input
            type="text"
            value={form.vereinContactPerson}
            onChange={(e) => setField('vereinContactPerson', e.target.value)}
            className="w-full border border-[#DAD9D9] rounded px-3 py-2 text-sm text-[#1A1A1A]"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 pt-2 border-t border-[#DAD9D9]">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">
            Lead-Source
          </label>
          <span className="inline-block px-2 py-1 text-xs font-medium bg-gray-100 text-[#1A1A1A] rounded">
            {sourceLabel(lead.source)}
          </span>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">
            Eingegangen
          </label>
          <span className="text-sm text-[#1A1A1A]">{formatDate(lead.receivedAt)}</span>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
          {error}
        </div>
      )}

      <div className="flex justify-end pt-2">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !isDirty}
          className="bg-[#E9FF57] text-[#1A1A1A] px-4 py-2 rounded font-medium hover:bg-[#d4ea4d] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? 'Speichert...' : 'Speichern'}
        </button>
      </div>
    </div>
  );
}
